import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Temporal } from '@js-temporal/polyfill';
import { useState } from 'react';

import { meQuery } from '../api/me.ts';
import {
  cancelMyLeave,
  myLeaveQuery,
  myUnavailabilityQuery,
  REASONS,
  retireMyUnavailability,
  whenLabel,
  type Leave,
} from '../api/time-off.ts';
import { Button } from '../components/button.tsx';
import { TimeOffForm, type TimeOffKind } from './time-off.tsx';

// CB-REQ CRW-02 at v1.9106, crew part 4D-3: a person's own unavailability and leave on My shifts,
// where their company allows them to enter each (their class's setting, or their own). Leave they
// ask for waits for the office unless its type needs no approval; they may cancel it before it
// starts. A first cut, to be redesigned with the operator's phone designs.

const STATE: Record<Leave['state'], string> = {
  pending: 'Waiting for the office',
  approved: 'Approved',
  declined: 'Declined',
  cancelled: 'Cancelled',
};

/** Whether leave has started, on its own zone's clock. */
function started(l: Leave): boolean {
  const start = l.when.days
    ? Temporal.PlainDate.from(l.when.days.from).toZonedDateTime(l.zone)
    : Temporal.PlainDateTime.from(l.when.times!.start).toZonedDateTime(l.zone);
  return Temporal.Instant.compare(start.toInstant(), Temporal.Now.instant()) <= 0;
}

export function MyTimeOff() {
  const me = useQuery(meQuery);
  const unavailability = useQuery(myUnavailabilityQuery);
  const leave = useQuery(myLeaveQuery);
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState<TimeOffKind | null>(null);
  const act = useMutation({
    mutationFn: (run: () => Promise<unknown>) => run(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['my'] }),
  });
  const zone = me.data?.tenant.defaultTz ?? 'Australia/Melbourne';
  const today = Temporal.Now.plainDateISO(zone);
  const drawn = {
    start: `${today.toString()}T00:00`,
    end: `${today.add({ days: 1 }).toString()}T00:00`,
  };
  const u = unavailability.data;
  const l = leave.data;
  const showUnavailable = u && (u.may || u.items.length > 0);
  const showLeave = l && (l.may || l.items.length > 0);
  if (!showUnavailable && !showLeave) return null;

  return (
    <div className="flex flex-col gap-6">
      {showUnavailable ? (
        <section aria-labelledby="my-unavailability" className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <h2 id="my-unavailability" className="section-title">
              Unavailability
            </h2>
            {u.may && adding !== 'unavailable' ? (
              <Button
                variant="quiet"
                className="small-btn"
                onClick={() => setAdding('unavailable')}
              >
                + Unavailable
              </Button>
            ) : null}
          </div>
          {adding === 'unavailable' ? (
            <TimeOffForm kind="unavailable" drawn={drawn} onDone={() => setAdding(null)} />
          ) : null}
          {u.items.length === 0 ? <p className="text-sm text-muted">None.</p> : null}
          <ul className="flex flex-col gap-2 text-sm">
            {u.items.map((x) => (
              <li key={x.id} className="panel flex flex-wrap items-baseline gap-2 p-3">
                <span className="font-semibold">{whenLabel(x.when)}</span>
                <span>{REASONS[x.reason]}</span>
                {x.repeatsWeekly ? <span className="tag">Weekly</span> : null}
                {x.note ? <span className="text-ink-soft">{x.note}</span> : null}
                {x.ownEntry ? (
                  <Button
                    variant="quiet"
                    className="small-btn ml-auto"
                    disabled={act.isPending}
                    onClick={() => act.mutate(() => retireMyUnavailability(x.id))}
                  >
                    Remove<span className="sr-only"> {whenLabel(x.when)}</span>
                  </Button>
                ) : (
                  <span className="text-xs text-muted">Entered by the office</span>
                )}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {showLeave ? (
        <section aria-labelledby="my-leave" className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <h2 id="my-leave" className="section-title">
              Leave
            </h2>
            {l.may && adding !== 'leave' ? (
              <Button variant="quiet" className="small-btn" onClick={() => setAdding('leave')}>
                + Leave
              </Button>
            ) : null}
          </div>
          {adding === 'leave' ? (
            <TimeOffForm kind="leave" drawn={drawn} onDone={() => setAdding(null)} />
          ) : null}
          {l.items.length === 0 ? <p className="text-sm text-muted">None.</p> : null}
          <ul className="flex flex-col gap-2 text-sm">
            {l.items.map((x) => (
              <li key={x.id} className="panel flex flex-wrap items-baseline gap-2 p-3">
                <span className="font-semibold">{whenLabel(x.when)}</span>
                <span>{x.type.name}</span>
                <span className={`tag ${x.state === 'pending' ? 'tag-warn' : ''}`}>
                  {STATE[x.state]}
                </span>
                {x.decisionNote ? <span className="text-ink-soft">“{x.decisionNote}”</span> : null}
                {(x.state === 'pending' || x.state === 'approved') && !started(x) ? (
                  <Button
                    variant="quiet"
                    className="small-btn ml-auto"
                    disabled={act.isPending}
                    onClick={() => act.mutate(() => cancelMyLeave(x.id))}
                  >
                    Cancel<span className="sr-only"> leave {whenLabel(x.when)}</span>
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {act.isError ? (
        <p role="alert" className="alert">
          {act.error.message}
        </p>
      ) : null}
    </div>
  );
}
